import React from "react";
import { formatCurrency } from "../../utils/formatters";
import { formatTjs } from "../../utils/priceUtils";
import NoTranslate from "./NoTranslate";

interface CurrencyProps {
  /** The amount to format. Can be number, string, null, or undefined to handle raw API data safely. */
  amount: number | string | null | undefined;

  /** TJS (Somoni) equivalent from backend — shown alongside USD when provided */
  tjsAmount?: number | null;

  /** Override the default currency if needed */
  currency?: string;

  /** Render as a different element (e.g., 'div', 'p') */
  as?: React.ElementType;

  /** Custom class names */
  className?: string;

  /** What to show if amount is invalid/null (defaults to null/empty) */
  fallback?: React.ReactNode;

  /** Any other props (onClick, style, etc.) */
  [key: string]: any;
}

const Currency: React.FC<CurrencyProps> = ({
  amount,
  tjsAmount,
  currency,
  as = "span",
  className = "",
  fallback = null,
  ...props
}) => {
  // 1. Safety Check: Ensure we have a valid number
  // valid: 0, 10.5, "100", "-5"
  // invalid: null, undefined, "abc", ""
  const isValid =
    amount !== null &&
    amount !== undefined &&
    amount !== "" &&
    !isNaN(Number(amount));

  // 2. Return fallback if invalid (prevents 'NaN' or errors)
  if (!isValid) {
    return <>{fallback}</>;
  }

  // 3. Convert to strict number for your utility
  const safeAmount = Number(amount);

  const hasTjs =
    tjsAmount !== null &&
    tjsAmount !== undefined &&
    !isNaN(Number(tjsAmount));

  return (
    <NoTranslate as={as} className={className} {...props}>
      {formatCurrency(safeAmount, currency)}
      {hasTjs && (
        <span style={{ color: "#666", marginLeft: 4 }}>
          / {formatTjs(Number(tjsAmount))} c.
        </span>
      )}
    </NoTranslate>
  );
};

export default Currency;
